import type { DateTime, SelectItem } from "@/definitions/types";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  DATE_FORMAT_STRING,
  DATETIME_FORMAT_STRING,
  DATETIME_SECONDS_FORMAT_STRING,
} from "@/constants/envs";
import { isEmpty, isValidValue } from "@/utils/rules";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// 서버가 주는 시각은 UTC 라 브라우저 시간대로 그리면 해외 접속자에게 날짜가 어긋난다 → KST 로 고정
const DISPLAY_TIMEZONE = "Asia/Seoul";
const EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** 시점이 확정된 값(Date · 타임스탬프 · Z/오프셋이 붙은 문자열)만 KST 로 바꾼다. 시간대 없는 문자열은 그대로 둔다. */
function toDisplayDayjs(value: NonNullable<DateTime>): dayjs.Dayjs {
  const isInstant =
    value instanceof Date ||
    typeof value === "number" ||
    EXPLICIT_OFFSET.test(value);
  return isInstant ? dayjs(value).tz(DISPLAY_TIMEZONE) : dayjs(value);
}

/**
 * 화면 표시 날짜 표준(69차 CM69-03) — 점 표기 `YYYY.MM.DD` · `YYYY.MM.DD HH:mm`.
 * env 의 `VITE_*_FORMAT_STRING` 은 입력값 해석(`isValidDateFormat`)에 쓰는 ISO 형식이라 표시 형식과 분리해 코드에 고정한다.
 */
const DISPLAY_DATE_FORMAT = "YYYY.MM.DD";
const DISPLAY_DATETIME_FORMAT = "YYYY.MM.DD HH:mm";
const DISPLAY_DATETIME_SECONDS_FORMAT = "YYYY.MM.DD HH:mm:ss";
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

/** 빈 값 표기 — 화면 전체에서 이 하나만 쓴다(em dash `—` 금지) */
export const EMPTY_MARK = "-";

/** 값이 없거나 읽을 수 없으면 null, 아니면 KST 로 바꾼 dayjs */
function toValidDisplayDayjs(value?: DateTime): dayjs.Dayjs | null {
  if (isEmpty(value) || !isValidDateFormat(value)) return null;
  return toDisplayDayjs(value as NonNullable<DateTime>);
}

/** KST `YYYY.MM.DD HH:mm:ss`. 빈 값 · 읽을 수 없는 값은 `-` */
export function formatDatetimeSeconds(
  value?: DateTime,
  format = DISPLAY_DATETIME_SECONDS_FORMAT,
): string {
  return toValidDisplayDayjs(value)?.format(format) ?? EMPTY_MARK;
}

/** KST `YYYY.MM.DD HH:mm`. 빈 값 · 읽을 수 없는 값은 `-` */
export function formatDatetime(
  value?: DateTime,
  format = DISPLAY_DATETIME_FORMAT,
): string {
  return toValidDisplayDayjs(value)?.format(format) ?? EMPTY_MARK;
}

/** KST `YYYY.MM.DD`. 빈 값 · 읽을 수 없는 값은 `-` */
export function formatDate(
  value?: DateTime,
  format = DISPLAY_DATE_FORMAT,
): string {
  return toValidDisplayDayjs(value)?.format(format) ?? EMPTY_MARK;
}

/** 표의 좁은 칸 전용 짧은 날짜 — KST `26.10.07(수)`. 빈 값은 `-` */
export function formatDateShort(value?: DateTime): string {
  const day = toValidDisplayDayjs(value);
  return day ? `${day.format("YY.MM.DD")}(${WEEKDAYS[day.day()]})` : EMPTY_MARK;
}

/** 오늘(KST)이면 `HH:mm`, 다른 날이면 `MM.DD HH:mm`. 빈 값은 `-` */
export function formatRelativeTime(value?: DateTime): string {
  const day = toValidDisplayDayjs(value);
  if (!day) return EMPTY_MARK;
  const today = dayjs().tz(DISPLAY_TIMEZONE).format("YYYYMMDD");
  return day.format(day.format("YYYYMMDD") === today ? "HH:mm" : "MM.DD HH:mm");
}

/** CSV · 내보내기 파일 전용 KST `YYYY-MM-DD`(기계 판독용 ISO). 빈 값은 빈 문자열(빈 칸) */
export function formatIsoDate(value?: DateTime): string {
  return toValidDisplayDayjs(value)?.format("YYYY-MM-DD") ?? "";
}

export function isValidDateFormat(value: DateTime | undefined): boolean {
  return (
    Boolean(value) &&
    (value instanceof Date ||
      dayjs(value, DATETIME_SECONDS_FORMAT_STRING).isValid() ||
      dayjs(value, "YYYYMMDDHHmmss").isValid() ||
      dayjs(value, DATETIME_FORMAT_STRING).isValid() ||
      dayjs(value, "YYYYMMDDHHmm").isValid() ||
      dayjs(value, DATE_FORMAT_STRING).isValid() ||
      dayjs(value, "YYYYMMDD").isValid())
  );
}

export function formatTextOfSelectItem<T = string>(
  codes: SelectItem<T>[] | null,
  value: T,
  defaultLabel?: string,
  useStrictComparison = false,
): string {
  if (!isValidValue(value)) {
    return "-";
  }

  const items = codes ?? [];
  const item = useStrictComparison
    ? items.find((item) => item.value === value)
    : items.find((item) => item.value == value); // 간혹 value 가 string|number 가 <T> 와 맞지 않는 케이스가 발생

  return item?.title ?? defaultLabel ?? String(value ?? "-");
}
export function getValueOfSelectItem<T = string>(
  codes: SelectItem<T>[] | null,
  text?: string | null,
): T | null {
  if (!text) {
    return null;
  }
  return (
    ((codes ?? []).find((item) => item.title === text)?.value as T) ?? null
  );
}

export function getFilenameFromUrl(url?: string): string {
  if (!url) {
    return "";
  }

  const startIndex = url.lastIndexOf("/") + "/".length;
  const endIndex = url.indexOf("?", startIndex);

  if (url.lastIndexOf("/") === -1) {
    return url;
  }

  if (endIndex === -1) {
    return url.split("/").pop() ?? "";
  }

  return url.slice(startIndex, endIndex);
}

/** 천 단위 구분 숫자. 브라우저 로캘과 무관하게 `ko-KR` 로 고정한다(CM69-05). 빈 값 · 숫자 아님은 `defaultValue` */
export function formatNumber(
  value: number | string | null | undefined,
  fractionDigits = 0,
  defaultValue = "-",
): string {
  if (isEmpty(value)) {
    return defaultValue;
  }
  const numValue = +value!;
  if (isNaN(numValue)) {
    return defaultValue;
  }
  return numValue.toLocaleString("ko-KR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/** 이전 표기(`3,812 KB`). 새 코드는 `formatBytes` 를 쓴다 */
export function formatFileSizeToKb(bytes?: number) {
  if (!bytes) {
    return 0 + " KB";
  }
  return formatNumber(bytes / 1024) + " KB";
}

/** 파일 크기 — 1MB 이상 `1.2MB`, 미만 `830KB`(0 초과는 최소 1KB, 빈 파일은 `0KB`, 공백 없음). 빈 값은 `-` */
export function formatBytes(bytes?: number | null): string {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return EMPTY_MARK;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  if (bytes <= 0) return "0KB";
  return `${formatNumber(Math.max(1, Math.round(bytes / 1024)))}KB`;
}

/**
 * 1~5 척도 점수(면접 평균 등). 0 · null 은 "채점 없음" 이라 `-`(실제 최솟값이 1.0 이다 — CM69-02),
 * 아니면 `4.2 / 5.0`. 좁은 칸은 `{ suffix: false }` 로 `4.2` 만.
 */
export function formatScore5(
  value?: number | null,
  { suffix = true }: { suffix?: boolean } = {},
): string {
  if (!value || isNaN(value)) return EMPTY_MARK;
  const text = value.toFixed(1);
  return suffix ? `${text} / 5.0` : text;
}

/**
 * 100점 척도 점수(종합점수 · 100점 환산 면접평균). 0 · null 은 `-`(CM69-01 · 04).
 * 표 칸은 숫자만(`84`), 문장 · 카드 값은 `{ unit: true }` 로 `84점`.
 */
export function formatScore100(
  value?: number | null,
  { unit = false }: { unit?: boolean } = {},
): string {
  if (!value || isNaN(value)) return EMPTY_MARK;
  const text = String(Math.round(value));
  return unit ? `${text}점` : text;
}

/** 연봉(만원 단위). 0 · null 은 미입력이라 `-`, 아니면 `5,000만원`(CM69-06) */
export function formatSalary(value?: number | null): string {
  return value && value > 0 ? `${formatNumber(value)}만원` : EMPTY_MARK;
}

/**
 * 백분율(0~100 값). 빈 값은 `-`, 아니면 반올림한 `84%`.
 * `{ cap99: true }` 면 100 미만 값은 반올림해도 최대 `99%` — 미완료가 100% 로 보이지 않게 한다(CM69-08).
 */
export function formatPercent(
  value?: number | null,
  { cap99 = false }: { cap99?: boolean } = {},
): string {
  if (value === undefined || value === null || isNaN(value)) return EMPTY_MARK;
  const rounded = Math.round(value);
  return `${cap99 && value < 100 ? Math.min(rounded, 99) : rounded}%`;
}

export function snakeToCamel(str?: string): string {
  if (!str) {
    return "";
  }
  return str.toLowerCase().replace(/_(\w)/g, (_, c: string) => c.toUpperCase());
}
// getTitleByValue는 formatTextOfSelectItem의 별칭으로 사용 (하위 호환성 유지)
export function getTitleByValue<T = string>(
  codes: SelectItem<T>[] | null,
  value: T,
  defaultLabel?: string,
): string {
  // 엄격한 비교(===)를 사용하는 버전으로 formatTextOfSelectItem 호출
  return formatTextOfSelectItem(codes, value, defaultLabel, true);
}
